/**
 * @fileoverview Weekly timesheet entry surface (MOR-906)
 *
 * Keyboard-first time entry: a Mon–Sun week strip with per-day totals, a
 * per-day entry list with inline editing, and an always-ready new-entry row.
 * Units are the firm's billing increment (6-minute units by default).
 * Job and category stay sticky between saves so consecutive entries on the
 * same matter take two keystrokes (units → narrative → Enter).
 *
 * Entries are drafts until submitted; "Submit week" sends every draft or
 * rejected entry in the visible week for partner approval. Approved and
 * submitted entries are locked (edit attempts are blocked server-side too).
 *
 * Dependencies:
 * - Convex: timesheets.entries.*, timesheets.seed.ensureSeeded
 */

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { useMutation } from "convex/react";
import {
  addDays,
  addMonths,
  endOfMonth,
  format,
  parseISO,
  startOfMonth,
  startOfWeek,
  subMonths,
} from "date-fns";
import {
  Building2,
  CalendarDays,
  Check,
  ChevronLeft,
  ChevronRight,
  Copy,
  Expand,
  List,
  Loader2,
  MessageSquare,
  Plus,
  Search,
  Send,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import type { TimeRoundingMode } from "@cvx/timesheets/timesheetSettings/schema";
import { roundUnits } from "@cvx/timesheets/units";
import { addMinutesToClock, rollOnStartTime } from "@cvx/timesheets/clock";
import { TimePicker } from "@/ui/composed/inputs/time-picker";
import { DictationButton } from "@/ui/composed/inputs/dictation-button";
import { useDictatedField } from "@/ui/hooks/use-dictated-field";

import { extractErrorMessage } from "@/utils/errors";
import { cn } from "@/utils/misc";
import { Badge, Button } from "@/ui/primitives";
import { Input } from "@/ui/primitives/input";
import { Spinner } from "@/ui/primitives/spinner";
import { Switch } from "@/ui/primitives/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/ui/composed/data-display/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/ui/composed/overlays/tooltip";
import { SearchableSelect } from "@/ui/composed/inputs/searchable-select";
import { FacetedFilter } from "@/ui/composed/filters/faceted-filter";
import { Checkbox } from "@/ui/primitives/checkbox";
import { DatePicker } from "@/ui/composed/inputs/date-picker";
import { formatAud } from "./format";
import { TimesheetCalendarView } from "./TimesheetCalendarView";
import { TimesheetMonthView } from "./TimesheetMonthView";
import { TimesheetChatSheet } from "./TimesheetChatSheet";
import { EntryDetailSheet } from "./EntryDetailSheet";
import { CategoryBadge, CategoryGlyph } from "./CategoryBadge";
import type { CategoryBadgeCategory } from "./CategoryBadge";
import { NARRATIVE_INPUT_PROPS } from "./narrativeCheck";
import { NarrativeWarningNote } from "./NarrativeWarningNote";
import { EntryTimeRange } from "./EntryTimeRange";
import { TimeOfDayToggle } from "./TimeOfDayToggle";
import { MissingTimesheetsNotice } from "./MissingTimesheetsNotice";
import { RateWorkingNote } from "./RateWorkingNote";
import { SubCategoryPicker } from "./SubCategoryPicker";
import { SubmitNarrativeReviewDialog } from "./SubmitNarrativeReviewDialog";
import { DraftSuggestionsPanel } from "./DraftSuggestionsPanel";
import { useTimeTracker } from "./TimeTrackerContext";
import {
  buildEntryFilter,
  computeFlaggedNarratives,
  computeDayBalances,
  computePeriodUnits,
  computeSubmitRange,
  computeSubmittableCount,
  computeWeekDays,
  endTimeFromStart,
  entriesInRange,
  groupEntriesByDay,
  isoDate,
  type CalendarPeriod,
  type EntryStatus,
  type ProjectCode,
  type WeekEntry,
} from "./weekGrid";

export type { WeekEntry } from "./weekGrid";

/** The always-ready new-entry row, lifted so the NL parser and AI
 *  suggestions can pre-fill it. Calendar slot clicks open the Log time
 *  dialog instead, so the practitioner stays on the calendar. */
interface EntryForm {
  jobId: string;
  categoryId: string;
  /** Optional sub-category metadata — never required (MOR-906 §4.1). */
  subCategory: string;
  units: string;
  narrative: string;
  billable: boolean;
  projectCode: ProjectCode;
  /** Optional HH:MM start — "" = unset. */
  startTime: string;
  /** Optional HH:MM end — "" = unset. Derived from start and units until edited. */
  endTime: string;
  /**
   * MOR-935: `startTime` was rolled on from the day's entries rather than
   * chosen. Kept IN the form (not a ref beside it) so a state updater can
   * always tell the two apart, whatever else re-rendered or remounted in
   * between (QA-53, QA-55).
   */
  startTimeAutoFilled: boolean;
}

const EMPTY_FORM: EntryForm = {
  jobId: "",
  categoryId: "",
  subCategory: "",
  units: "",
  narrative: "",
  billable: true,
  projectCode: "post",
  startTime: "",
  endTime: "",
  startTimeAutoFilled: false,
};

const STATUS_BADGE: Record<EntryStatus, { label: string; className: string }> =
  {
    draft: { label: "Draft", className: "bg-muted text-muted-foreground" },
    submitted: {
      label: "Submitted",
      className:
        "bg-blue-100 text-blue-800 dark:bg-blue-950/50 dark:text-blue-300",
    },
    approved: {
      label: "Approved",
      className:
        "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300",
    },
    rejected: {
      label: "Rejected",
      className: "bg-red-100 text-red-800 dark:bg-red-950/50 dark:text-red-300",
    },
  };

const iso = isoDate;

export function WeeklyTimesheet({
  orgId,
  jobId,
  viewerUserId,
  canClearMissingNotice = false,
}: {
  orgId: Id<"organizations">;
  /** When set, the surface is locked to a single matter (per-job tab). */
  jobId?: Id<"jobs">;
  /** The signed-in user, so an owner or admin can clear their own notice. */
  viewerUserId?: Id<"users">;
  /** Org owners and admins only. */
  canClearMissingNotice?: boolean;
}) {
  const { openQuickEntry } = useTimeTracker();
  const todayIso = iso(new Date());
  const [weekStart, setWeekStart] = useState(() =>
    iso(startOfWeek(new Date(), { weekStartsOn: 1 })),
  );
  const [selectedDate, setSelectedDate] = useState(todayIso);
  /** Period shared by the list and calendar views. */
  const [period, setPeriod] = useState<CalendarPeriod>("day");
  const [historyStart, setHistoryStart] = useState(() =>
    iso(subMonths(new Date(), 12)),
  );
  const [historyEnd, setHistoryEnd] = useState(todayIso);

  const weekDays = useMemo(() => computeWeekDays(weekStart), [weekStart]);
  const weekEnd = weekDays[6];

  // Month window anchored to the selected day, which always sits inside the
  // visible week. It used to anchor on the week's Monday: the month view
  // sets the week to the one holding the 1st, whose Monday can fall in the
  // month before, so "Previous month" skipped a month (QA-74). Month
  // navigation selects the 1st of the month it lands on for the same reason.
  const monthAnchor = selectedDate;
  const monthStartIso = iso(startOfMonth(parseISO(monthAnchor)));
  const monthEndIso = iso(endOfMonth(parseISO(monthAnchor)));

  // Keep the selected day inside the visible week when navigating.
  useEffect(() => {
    if (!weekDays.includes(selectedDate)) {
      setSelectedDate(weekDays.includes(todayIso) ? todayIso : weekDays[0]);
    }
  }, [weekDays, selectedDate, todayIso]);

  // Seed the default insolvency categories on first visit (idempotent).
  const ensureSeeded = useMutation(api.timesheets.seed.ensureSeeded);
  const clearMissingTimesheetNotice = useMutation(
    api.timesheets.profiles.clearMissingTimesheetNotice,
  );
  useEffect(() => {
    ensureSeeded({ orgId }).catch(() => {
      // Non-fatal: the categories list simply stays empty until an admin
      // opens Timesheet Settings.
    });
  }, [ensureSeeded, orgId]);

  const { data: context } = useQuery(
    convexQuery(api.timesheets.entries.entryContext, {
      orgId,
      asOfDate: selectedDate,
      // Keep the matter resolvable even if it's archived/closed.
      ...(jobId ? { includeJobId: jobId } : {}),
    }),
  );
  // Always fetch the union of the visible week and its month, whatever the
  // period: day, week and month then share one subscription, so switching
  // between them is instant instead of opening a new query. Bounded
  // server-side.
  const fetchStart = monthStartIso < weekStart ? monthStartIso : weekStart;
  const fetchEnd = monthEndIso > weekEnd ? monthEndIso : weekEnd;
  const { data: weekData } = useQuery({
    ...convexQuery(api.timesheets.entries.myWeek, {
      orgId,
      startDate: fetchStart,
      endDate: fetchEnd,
      ...(jobId ? { jobId } : {}),
    }),
    enabled: period !== "all",
    // Navigating to another week keeps the current entries on screen while
    // the next range loads, rather than dropping back to the spinner.
    placeholderData: keepPreviousData,
  });
  const { data: historyData } = useQuery({
    ...convexQuery(api.timesheets.entries.myHistory, {
      orgId,
      startDate: historyStart,
      endDate: historyEnd,
      ...(jobId ? { jobId } : {}),
    }),
    enabled: period === "all",
    placeholderData: keepPreviousData,
  });
  const entries = period === "all" ? historyData : weekData;

  // MOR-1459: what this person is expected to record on each day of the
  // visible week, from their work schedule (or the firm figure). Not asked
  // for on the per-job view: a full-day target is meaningless against one
  // matter.
  const { data: expected } = useQuery({
    ...convexQuery(api.timesheets.missingTimesheets.myExpectedUnits, {
      orgId,
      startDate: weekStart,
      endDate: weekEnd,
    }),
    enabled: !jobId,
    placeholderData: keepPreviousData,
  });
  // MOR-1461: the days they still owe, so the page says so up front.
  const { data: missing } = useQuery({
    ...convexQuery(api.timesheets.missingTimesheets.myMissingDays, {
      orgId,
      today: todayIso,
    }),
    enabled: !jobId,
  });
  // MOR-1039: the firm's narrative-flagging setting. When off, the submit
  // review never opens and no entry is flagged; when on, the firm's own
  // guidance is shown against a thin narrative.
  const { data: narrativeSettings } = useQuery(
    convexQuery(api.timesheets.settings.getForOrg, { orgId }),
  );
  const narrativeFlaggingEnabled =
    narrativeSettings?.narrativeFlaggingEnabled ?? false;
  const narrativeContext = narrativeSettings?.narrativeContext ?? "";

  const submitRange = useMutation(api.timesheets.entries.submitRange);
  const [submitting, setSubmitting] = useState(false);
  const [narrativeReviewOpen, setNarrativeReviewOpen] = useState(false);
  const [view, setView] = useState<"day" | "calendar">("day");
  const setMyPreferences = useMutation(
    api.timesheets.profiles.setMyPreferences,
  );
  // Remember the list / calendar choice per person (MOR-935). The stored
  // value only changes when this person changes it, so following it never
  // overrides a click of theirs.
  const preferredEntryView = context?.preferredEntryView;
  useEffect(() => {
    if (preferredEntryView) setView(preferredEntryView);
  }, [preferredEntryView]);
  const chooseView = (next: "day" | "calendar") => {
    setView(next);
    void setMyPreferences({ orgId, preferredEntryView: next }).catch(() => {
      // A preference that fails to save is not worth interrupting entry for.
    });
  };
  const [form, setForm] = useState<EntryForm>(EMPTY_FORM);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatSeed, setChatSeed] = useState<string | undefined>(undefined);
  const [detailEntryId, setDetailEntryId] = useState<Id<"timeEntries"> | null>(
    null,
  );
  // View filters — a client-side lens over list and calendar. Totals, gaps
  // and the submit button stay unfiltered: they speak about the whole
  // period, not the current slice.
  const [filterSearch, setFilterSearch] = useState("");
  const [filterJobIds, setFilterJobIds] = useState<string[]>([]);
  const [filterCategoryIds, setFilterCategoryIds] = useState<string[]>([]);
  const [filterStatuses, setFilterStatuses] = useState<string[]>([]);
  const [filterBillable, setFilterBillable] = useState<string[]>([]);
  const hasFilters =
    filterSearch.trim() !== "" ||
    filterJobIds.length > 0 ||
    filterCategoryIds.length > 0 ||
    filterStatuses.length > 0 ||
    filterBillable.length > 0;
  const clearFilters = () => {
    setFilterSearch("");
    setFilterJobIds([]);
    setFilterCategoryIds([]);
    setFilterStatuses([]);
    setFilterBillable([]);
  };

  const unitMinutes = context?.unitMinutes ?? 6;
  const toHours = (units: number) => (units * unitMinutes) / 60;
  const fmtHours = (units: number) =>
    `${toHours(units).toLocaleString("en-AU", { maximumFractionDigits: 2 })}h`;

  const entriesByDay = useMemo(
    () => groupEntriesByDay((entries ?? []) as WeekEntry[]),
    [entries],
  );

  // One filter predicate for all views. Generic so team-scope entries
  // (which carry userName) keep their type through the filter.
  const applyFilters = useMemo(
    () =>
      buildEntryFilter({
        search: filterSearch,
        jobIds: filterJobIds,
        categoryIds: filterCategoryIds,
        statuses: filterStatuses,
        billable: filterBillable,
      }),
    [
      filterSearch,
      filterJobIds,
      filterCategoryIds,
      filterStatuses,
      filterBillable,
    ],
  );

  const filterJobOptions = useMemo(
    () =>
      (context?.jobs ?? []).map((j) => ({
        value: j._id as string,
        label: j.jobCode ? `${j.jobCode} — ${j.name}` : j.name,
      })),
    [context?.jobs],
  );
  const filterCategoryOptions = useMemo(
    () =>
      (context?.categories ?? []).map((c) => ({
        value: c._id as string,
        label: c.name,
      })),
    [context?.categories],
  );

  const dayEntries = entriesByDay.get(selectedDate) ?? [];
  // Week-scoped subset: the fetch may span a whole month, but the strip,
  // totals, gaps and submit button always speak about the visible week.
  const weekEntries = useMemo(
    () => entriesInRange((entries ?? []) as WeekEntry[], weekStart, weekEnd),
    [entries, weekStart, weekEnd],
  );
  // Submit follows the visible period — day, week or month — so the button
  // says exactly what it sends.
  const activePeriod: CalendarPeriod = period;
  const { start: submitStart, end: submitEnd } = computeSubmitRange({
    period: activePeriod,
    historyStart,
    historyEnd,
    monthStart: monthStartIso,
    monthEnd: monthEndIso,
    selectedDate,
    weekStart,
    weekEnd,
  });
  const submittableCount = computeSubmittableCount(
    (entries ?? []) as WeekEntry[],
    activePeriod,
    submitStart,
    submitEnd,
  );
  const periodUnits = computePeriodUnits(
    (entries ?? []) as WeekEntry[],
    submitStart,
    submitEnd,
  );
  // MOR-1039: thin narratives get one last look before they go for approval,
  // but only when the firm has switched narrative flagging on.
  const flaggedNarratives = narrativeFlaggingEnabled
    ? computeFlaggedNarratives(
        (entries ?? []) as WeekEntry[],
        activePeriod,
        submitStart,
        submitEnd,
        unitMinutes,
        narrativeContext,
      )
    : [];

  // Daily balance (MOR-1459): each scheduled day of the week against what
  // was recorded. Empty on the per-job view (no schedule query there).
  const expectedByDay = useMemo(
    () => new Map((expected?.days ?? []).map((d) => [d.date, d.expectedUnits])),
    [expected],
  );
  const balanceByDay = useMemo(
    () =>
      new Map(
        computeDayBalances(weekDays, todayIso, entriesByDay, expectedByDay).map(
          (b) => [b.day, b],
        ),
      ),
    [weekDays, todayIso, entriesByDay, expectedByDay],
  );
  // The period's target, for "19h of 40h" in the header. Only the week and
  // day periods sit inside the schedule fetched.
  const expectedPeriodUnits =
    activePeriod === "week" || activePeriod === "day"
      ? [...expectedByDay].reduce(
          (sum, [day, units]) =>
            day >= submitStart && day <= submitEnd ? sum + units : sum,
          0,
        )
      : 0;

  const handleSubmitPeriod = async () => {
    setSubmitting(true);
    try {
      const count = await submitRange({
        orgId,
        startDate: submitStart,
        endDate: submitEnd,
        ...(jobId ? { jobId } : {}),
      });
      toast.success(
        count === 1
          ? "1 entry submitted for approval."
          : `${count} entries submitted for approval.`,
      );
    } catch (err) {
      toast.error(
        extractErrorMessage(err, `Could not submit the ${activePeriod}.`),
      );
    } finally {
      setSubmitting(false);
      setNarrativeReviewOpen(false);
    }
  };

  if (context === undefined || entries === undefined) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner className="text-muted-foreground" />
      </div>
    );
  }

  // Period-aware navigation: the arrows move by the active period.
  const navigate = (direction: 1 | -1) => {
    if (activePeriod === "all") {
      setHistoryStart(iso(addMonths(parseISO(historyStart), direction * 12)));
      setHistoryEnd(iso(addMonths(parseISO(historyEnd), direction * 12)));
      return;
    }
    if (activePeriod === "month") {
      const next = startOfMonth(addMonths(parseISO(monthAnchor), direction));
      setSelectedDate(iso(next));
      setWeekStart(iso(startOfWeek(next, { weekStartsOn: 1 })));
      return;
    }
    if (activePeriod === "day") {
      const next = iso(addDays(parseISO(selectedDate), direction));
      setSelectedDate(next);
      setWeekStart(iso(startOfWeek(parseISO(next), { weekStartsOn: 1 })));
      return;
    }
    setWeekStart(iso(addDays(parseISO(weekStart), direction * 7)));
  };
  const rangeLabel =
    activePeriod === "all"
      ? `${format(parseISO(historyStart), "d MMM yyyy")} – ${format(parseISO(historyEnd), "d MMM yyyy")}`
      : activePeriod === "month"
        ? format(parseISO(monthAnchor), "MMMM yyyy")
        : activePeriod === "day"
          ? format(parseISO(selectedDate), "EEE d MMM yyyy")
          : `${format(parseISO(weekStart), "d MMM")} – ${format(parseISO(weekEnd), "d MMM yyyy")}`;

  return (
    <div className="flex w-full flex-col gap-4">
      {/* Period navigation */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            className="size-8"
            onClick={() => navigate(-1)}
            aria-label={`Previous ${activePeriod}`}
          >
            <ChevronLeft className="size-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="size-8"
            onClick={() => navigate(1)}
            aria-label={`Next ${activePeriod}`}
          >
            <ChevronRight className="size-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-8"
            onClick={() => {
              setWeekStart(iso(startOfWeek(new Date(), { weekStartsOn: 1 })));
              setSelectedDate(todayIso);
              setHistoryStart(iso(subMonths(new Date(), 12)));
              setHistoryEnd(todayIso);
            }}
          >
            Today
          </Button>
          {period === "all" ? (
            <div className="ml-1 flex items-center gap-1.5">
              <DatePicker
                value={historyStart}
                onChange={(value) => {
                  if (!value) return;
                  setHistoryStart(value);
                  if (value > historyEnd) setHistoryEnd(value);
                }}
                className="w-[9.5rem]"
                inputClassName="h-8 text-sm"
              />
              <span className="text-muted-foreground">–</span>
              <DatePicker
                value={historyEnd}
                onChange={(value) => {
                  if (!value) return;
                  setHistoryEnd(value);
                  if (value < historyStart) setHistoryStart(value);
                }}
                className="w-[9.5rem]"
                inputClassName="h-8 text-sm"
              />
            </div>
          ) : (
            <span className="ml-1 text-sm font-medium">{rangeLabel}</span>
          )}
          <div className="ml-2 flex items-center rounded-md border border-border">
            {(["day", "week", "month", "all"] as const).map((p, i, arr) => (
              <Button
                key={p}
                variant={period === p ? "secondary" : "ghost"}
                size="sm"
                className={cn(
                  "h-8 capitalize",
                  i === 0 && "rounded-r-none",
                  i === arr.length - 1 && "rounded-l-none",
                  i > 0 && i < arr.length - 1 && "rounded-none",
                )}
                onClick={() => {
                  setPeriod(p);
                  if (p === "all") {
                    setView("day");
                    setFilterStatuses((prev) =>
                      prev.filter((s) => s === "submitted" || s === "approved"),
                    );
                  }
                }}
              >
                {p}
              </Button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* The stopwatch lives in the app header (HeaderTimeTracker) so it
              stays visible and stoppable from every route. */}
          <TimeOfDayToggle
            orgId={orgId}
            showTimeOfDay={context.showTimeOfDay}
          />
          {period !== "all" && (
            <div className="flex items-center rounded-md border border-border">
              <Button
                variant={view === "day" ? "secondary" : "ghost"}
                size="sm"
                className="h-8 rounded-r-none"
                onClick={() => chooseView("day")}
                aria-label="List view"
              >
                <List className="size-4" />
                List
              </Button>
              <Button
                variant={view === "calendar" ? "secondary" : "ghost"}
                size="sm"
                className="h-8 rounded-l-none"
                onClick={() => chooseView("calendar")}
                aria-label="Calendar view"
              >
                <CalendarDays className="size-4" />
                Calendar
              </Button>
            </div>
          )}
          <span className="text-sm capitalize text-muted-foreground">
            {activePeriod === "all" ? "Range" : activePeriod} total:{" "}
            <span className="font-medium text-foreground">
              {fmtHours(periodUnits)}
            </span>
            {expectedPeriodUnits > 0 && (
              <span className="normal-case">
                {" "}
                of {fmtHours(expectedPeriodUnits)}
              </span>
            )}
          </span>
          {period !== "all" && (
            <Button
              size="sm"
              className="h-8"
              disabled={submittableCount === 0 || submitting}
              onClick={() => {
                if (flaggedNarratives.length > 0) setNarrativeReviewOpen(true);
                else void handleSubmitPeriod();
              }}
              title={`Submit every draft entry from ${rangeLabel} for approval`}
            >
              {submitting ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Send className="size-4" />
              )}
              Submit {activePeriod}
              {submittableCount > 0 && (
                <span className="ml-1 rounded-full bg-primary-foreground/20 px-1.5 text-xs">
                  {submittableCount}
                </span>
              )}
            </Button>
          )}
          {/* Opens the timesheet chat assistant — log draft time or ask about
              entries. Replaces the quick-entry bar's old expand button. */}
          <Button
            variant="ghost"
            size="icon"
            className="size-8 text-muted-foreground"
            onClick={() => setChatOpen(true)}
            aria-label="Ask the timesheet assistant"
            title="Ask the timesheet assistant — log draft time or ask about your entries"
          >
            <MessageSquare className="size-4" />
          </Button>
        </div>
      </div>

      {/* View filters — narrow every view to the matching entries. Totals
          and Submit stay unfiltered: they describe the whole period. */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={filterSearch}
            onChange={(e) => setFilterSearch(e.target.value)}
            placeholder="Search entries…"
            className="h-8 w-52 pl-8 text-sm"
            aria-label="Search entries"
          />
        </div>
        {/* The per-job view is already one matter, so the Matter filter
            would be redundant. */}
        {!jobId && (
          <FacetedFilter
            title="Matter"
            options={filterJobOptions}
            selectedValues={filterJobIds}
            onSelectionChange={setFilterJobIds}
          />
        )}
        <FacetedFilter
          title="Category"
          options={filterCategoryOptions}
          selectedValues={filterCategoryIds}
          onSelectionChange={setFilterCategoryIds}
          renderLabel={(option) => {
            const category = context.categories.find(
              (c) => (c._id as string) === option.value,
            );
            return (
              <span className="flex items-center gap-1.5">
                <CategoryGlyph color={category?.color} icon={category?.icon} />
                {option.label}
              </span>
            );
          }}
        />
        <FacetedFilter
          title="Status"
          options={[
            { value: "draft", label: "Draft" },
            { value: "submitted", label: "Submitted" },
            { value: "approved", label: "Approved" },
            { value: "rejected", label: "Rejected" },
          ]}
          selectedValues={filterStatuses}
          onSelectionChange={setFilterStatuses}
        />
        <FacetedFilter
          title="Billable"
          options={[
            { value: "billable", label: "Billable" },
            { value: "non_billable", label: "Non-billable" },
          ]}
          selectedValues={filterBillable}
          onSelectionChange={setFilterBillable}
        />
        {hasFilters && (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 text-xs text-muted-foreground"
            onClick={clearFilters}
          >
            <X className="size-3.5" />
            Clear filters
          </Button>
        )}
      </div>

      {/* MOR-1461: the days this person still owes, kept in front of them
          until entered. Never shown on the per-job view. */}
      {!jobId && missing && missing.count > 0 && (
        <MissingTimesheetsNotice
          count={missing.count}
          shortHours={toHours(missing.shortUnits)}
          firstDay={missing.firstDay}
          onGoToFirstDay={(day) => {
            setWeekStart(iso(startOfWeek(parseISO(day), { weekStartsOn: 1 })));
            setSelectedDate(day);
            setPeriod("day");
          }}
          onClear={
            canClearMissingNotice && viewerUserId
              ? () => {
                  void clearMissingTimesheetNotice({
                    orgId,
                    userId: viewerUserId,
                    today: todayIso,
                  })
                    .then(() =>
                      toast.success(
                        "Missing timesheets notice cleared. Time is still expected.",
                      ),
                    )
                    .catch((err: unknown) =>
                      toast.error(
                        extractErrorMessage(
                          err,
                          "Could not clear the missing timesheets notice.",
                        ),
                      ),
                    );
                }
              : undefined
          }
        />
      )}

      {/* Day strip — the week's day navigator; hidden in month period
          where its seven days don't represent the visible range. Week
          period lights every day, because that is the range the list,
          totals and Submit button are talking about. */}
      {activePeriod !== "month" && activePeriod !== "all" && (
        <div
          className={cn(
            "grid grid-cols-7",
            activePeriod === "week" ? "gap-1" : "gap-2",
          )}
        >
          {weekDays.map((day) => {
            const dayUnits = (entriesByDay.get(day) ?? []).reduce(
              (sum, e) => sum + e.units,
              0,
            );
            const isSelected = day === selectedDate;
            const isToday = day === todayIso;
            const balance = balanceByDay.get(day);
            const weekMode = activePeriod === "week";
            const isCovered = weekMode || isSelected;
            return (
              <button
                key={day}
                type="button"
                onClick={() => {
                  setSelectedDate(day);
                  // A click names a single day, so leave week range mode.
                  if (weekMode) setPeriod("day");
                }}
                aria-current={isToday ? "date" : undefined}
                className={cn(
                  "flex flex-col items-center rounded-lg border px-2 py-2 text-sm transition-colors",
                  isCovered
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border bg-background hover:bg-muted",
                  isToday && !isCovered && "border-primary/40 bg-primary/5",
                )}
              >
                <span
                  className={cn(
                    "text-xs",
                    isCovered
                      ? "text-primary-foreground/80"
                      : isToday
                        ? "font-medium text-primary"
                        : "text-muted-foreground",
                  )}
                >
                  {isToday ? "Today" : format(parseISO(day), "EEE")}
                </span>
                <span
                  className={cn(
                    "font-semibold",
                    isToday && !isCovered && "text-primary",
                  )}
                >
                  {format(parseISO(day), "d")}
                </span>
                <span
                  className={cn(
                    "mt-0.5 text-xs tabular-nums",
                    isCovered
                      ? "text-primary-foreground/80"
                      : dayUnits > 0
                        ? "text-foreground"
                        : "text-muted-foreground/50",
                  )}
                >
                  {dayUnits > 0 ? fmtHours(dayUnits) : "—"}
                  {balance && (
                    <span
                      className={cn(
                        isCovered
                          ? "text-primary-foreground/60"
                          : "text-muted-foreground/70",
                      )}
                    >
                      {" "}
                      of {fmtHours(balance.expectedUnits)}
                    </span>
                  )}
                </span>
                {balance && balance.missingUnits > 0 && (
                  <span
                    className={cn(
                      "mt-0.5 text-[10px] font-medium tabular-nums",
                      isCovered ? "text-amber-200" : "text-amber-600",
                    )}
                    aria-label="Unaccounted time"
                  >
                    short {fmtHours(balance.missingUnits)}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Automatic drafting (MOR-1041): evidence-linked suggestions from
          Morgan activity and, when connected, Outlook calendar, sent mail
          and Microsoft 365 documents. Hidden on All — a read-only history. */}
      {period !== "all" && (
        <DraftSuggestionsPanel
          orgId={orgId}
          weekStart={weekStart}
          weekEnd={weekEnd}
          fmtHours={fmtHours}
          unitMinutes={unitMinutes}
          roundingMode={context?.roundingMode ?? "up"}
          jobs={(context?.jobs ?? []).map((j) => ({
            _id: j._id,
            name: j.jobCode ? `${j.jobCode} — ${j.name}` : j.name,
          }))}
          categories={(context?.categories ?? []).map((c) => ({
            _id: c._id,
            name: c.name,
          }))}
        />
      )}

      {view === "calendar" && period === "month" ? (
        <TimesheetMonthView
          monthAnchor={weekStart}
          entries={applyFilters(
            ((entries ?? []) as WeekEntry[]).filter(
              (e) => e.date >= monthStartIso && e.date <= monthEndIso,
            ),
          )}
          todayIso={todayIso}
          fmtHours={fmtHours}
          categoryColor={(categoryId) =>
            context.categories.find((c) => c._id === categoryId)?.color
          }
          onDayClick={(date) => {
            setSelectedDate(date);
            setWeekStart(iso(startOfWeek(parseISO(date), { weekStartsOn: 1 })));
            setView("day");
          }}
        />
      ) : view === "calendar" ? (
        <TimesheetCalendarView
          weekDays={period === "day" ? [selectedDate] : weekDays}
          entries={applyFilters((entries ?? []) as WeekEntry[])}
          unitMinutes={unitMinutes}
          roundingMode={context?.roundingMode ?? "up"}
          todayIso={todayIso}
          categoryVisual={(categoryId) => {
            const category = context.categories.find(
              (c) => c._id === categoryId,
            );
            return { color: category?.color, icon: category?.icon };
          }}
          onSlotSelect={({ date, startTime, units }) => {
            setSelectedDate(date);
            openQuickEntry({
              date,
              startTime,
              endTime: endTimeFromStart(startTime, units * unitMinutes),
              units,
              ...(jobId ? { jobId } : {}),
            });
          }}
          onEntryClick={(entryId) => setDetailEntryId(entryId)}
        />
      ) : (
        (() => {
          const periodEntries = applyFilters(
            period === "all"
              ? ((entries ?? []) as WeekEntry[])
              : period === "month"
                ? ((entries ?? []) as WeekEntry[]).filter(
                    (e) => e.date >= monthStartIso && e.date <= monthEndIso,
                  )
                : period === "week"
                  ? weekEntries
                  : dayEntries,
          );
          // Drafts (and rejected) live in their own editable table; the
          // submitted/approved pipeline is a separate read-only table below.
          const draftEntries = periodEntries.filter(
            (e) => e.status === "draft" || e.status === "rejected",
          );
          const lockedEntries = periodEntries.filter(
            (e) => e.status === "submitted" || e.status === "approved",
          );
          return (
            <>
              {/* All is a history: drafts show and stay editable, but new
                  time is entered on a day, week or month. */}
              {(period !== "all" || draftEntries.length > 0) && (
                <DayEntriesTable
                  date={selectedDate}
                  entries={draftEntries}
                  orgId={orgId}
                  allowCompose={period !== "all"}
                  rollOnStart={
                    context.showTimeOfDay &&
                    dayEntries.some((entry) => entry.startTime)
                      ? rollOnStartTime(dayEntries, context.unitMinutes)
                      : null
                  }
                  showDateColumn={period !== "day"}
                  rangeLabel={
                    period !== "day" ? `Drafts — ${rangeLabel}` : undefined
                  }
                  context={context}
                  fmtHours={fmtHours}
                  form={form}
                  setForm={setForm}
                  onOpenDetail={(id) => setDetailEntryId(id)}
                  lockedJobId={jobId}
                />
              )}
              {(lockedEntries.length > 0 || period === "all") && (
                <LockedEntriesTable
                  entries={lockedEntries}
                  showDateColumn={period !== "day"}
                  dateFormat={period === "all" ? "EEE d MMM yyyy" : "EEE d"}
                  title={
                    period === "all"
                      ? `Submitted & approved — ${rangeLabel}`
                      : undefined
                  }
                  emptyLabel={
                    period === "all"
                      ? "No submitted or approved entries in this range."
                      : undefined
                  }
                  context={context}
                  fmtHours={fmtHours}
                  onOpenDetail={(id) => setDetailEntryId(id)}
                />
              )}
            </>
          );
        })()
      )}

      <TimesheetChatSheet
        open={chatOpen}
        onOpenChange={(next) => {
          setChatOpen(next);
          if (!next) setChatSeed(undefined);
        }}
        orgId={orgId}
        initialInput={chatSeed}
      />

      <SubmitNarrativeReviewDialog
        open={narrativeReviewOpen}
        flagged={flaggedNarratives}
        periodLabel={activePeriod}
        submitting={submitting}
        fmtHours={fmtHours}
        guidanceText={narrativeContext}
        onOpenChange={setNarrativeReviewOpen}
        onSubmitAnyway={() => void handleSubmitPeriod()}
      />

      <EntryDetailSheet
        entryId={detailEntryId}
        open={detailEntryId !== null}
        onOpenChange={(next) => {
          if (!next) setDetailEntryId(null);
        }}
        unitMinutes={unitMinutes}
        categoryVisual={(categoryId) => {
          const category = context.categories.find((c) => c._id === categoryId);
          return { color: category?.color, icon: category?.icon };
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Day entry table with inline editing + new-entry row
// ---------------------------------------------------------------------------

interface ContextCategory {
  _id: Id<"timeCategories">;
  name: string;
  color?: CategoryBadgeCategory["color"];
  icon?: string;
  ipsCode?: string;
  subCodes: Array<{ code: string; name?: string }>;
}

interface EntryContext {
  jobs: { _id: Id<"jobs">; name: string; jobCode?: string; status: string }[];
  categories: ContextCategory[];
  /** Firm-scoped activities (non-chargeable firm overhead). */
  firmActivities: ContextCategory[];
  unitMinutes: number;
  /** MOR-1016 — firm's part-unit rounding direction. */
  roundingMode: TimeRoundingMode;
  recentJobIds: Id<"jobs">[];
  canApprove: boolean;
  /** MOR-935 — the caller logs against the clock. */
  showTimeOfDay: boolean;
  hourlyRate?: number;
  /** The caller's rate on each matter that is not on Standard terms. */
  jobRates: { jobId: Id<"jobs">; hourlyRate?: number }[];
}

function DayEntriesTable({
  date,
  entries,
  showDateColumn = false,
  rangeLabel,
  context,
  orgId,
  fmtHours,
  form,
  setForm,
  onOpenDetail,
  lockedJobId,
  rollOnStart,
  allowCompose = true,
}: {
  date: string;
  entries: WeekEntry[];
  /** False on the All history view: no new-entry row. */
  allowCompose?: boolean;
  /** Week/month list periods add a Date column and range header. */
  showDateColumn?: boolean;
  rangeLabel?: string;
  context: EntryContext;
  orgId: Id<"organizations">;
  fmtHours: (units: number) => string;
  form: EntryForm;
  setForm: Dispatch<SetStateAction<EntryForm>>;
  onOpenDetail: (entryId: Id<"timeEntries">) => void;
  /** Per-job view: the compose row's matter is fixed to this job. */
  lockedJobId?: Id<"jobs">;
  /**
   * MOR-935: where a new entry on this day starts, from EVERY entry on the
   * day (submitted and approved ones live in another table but still end at
   * a time). `null` when this person posts to the day.
   */
  rollOnStart: string | null;
}) {
  const createEntry = useMutation(api.timesheets.entries.createEntry);
  const updateEntry = useMutation(api.timesheets.entries.updateEntry);
  const deleteEntry = useMutation(api.timesheets.entries.deleteEntry);
  const updateEntries = useMutation(api.timesheets.entries.updateEntries);
  const deleteEntries = useMutation(api.timesheets.entries.deleteEntries);
  const submitEntry = useMutation(api.timesheets.entries.submitEntry);

  // Bulk selection over the day's editable (draft/rejected) entries.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const editableEntries = entries.filter(
    (e) => e.status === "draft" || e.status === "rejected",
  );
  const selectedIds = [...selected].filter((id) =>
    editableEntries.some((e) => (e._id as string) === id),
  ) as unknown as Id<"timeEntries">[];

  const toggleSelected = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const runBulk = async (fn: () => Promise<number>, label: string) => {
    try {
      const count = await fn();
      toast.success(`${count} ${count === 1 ? "entry" : "entries"} ${label}.`);
      setSelected(new Set());
    } catch (err) {
      toast.error(extractErrorMessage(err, "Bulk update failed."));
    }
  };

  // The new-entry form lives in the parent (EntryForm) so the NL parser
  // and AI suggestions can pre-fill it. Job/category/billable/project stay
  // sticky after a save so back-to-back entries on the same matter are two
  // keystrokes.
  const { jobId, categoryId, units, narrative, billable, projectCode } = form;
  const unitsNumber = Number(units);
  const unitsValid = Number.isFinite(unitsNumber) && unitsNumber >= 1;
  // Live rate for the compose row. A matter on a rate card or with frozen
  // rates has its own figure (MOR-1038); every other matter uses the caller's
  // Standard rate — or, until that query lands, any entry already loaded.
  const selectedJobRate = context.jobRates.find((r) => r.jobId === jobId);
  const previewRate = selectedJobRate
    ? selectedJobRate.hourlyRate
    : (context.hourlyRate ??
      entries.find((e) => e.hourlyRate !== undefined)?.hourlyRate);
  const previewAmount =
    unitsValid && previewRate !== undefined && billable
      ? ((unitsNumber * context.unitMinutes) / 60) * previewRate
      : undefined;
  const [saving, setSaving] = useState(false);
  /** The end field was edited, so it no longer follows start plus units. */
  const [endTouched, setEndTouched] = useState(false);
  /** Start and end stay hidden on the empty row until the person adds them. */
  const [timesOpen, setTimesOpen] = useState(false);
  const unitsRef = useRef<HTMLInputElement>(null);
  const narrativeRef = useRef<HTMLInputElement>(null);

  // Roll on from the day's last timed entry (MOR-935): the compose row's
  // start follows `rollOnStart` for as long as nobody has chosen a start by
  // hand. An empty day stays blank — 09:00 is not filled in — so a carried
  // start is cleared instead (QA-55). The value is also dropped when Show
  // times goes off (`rollOnStart` null, QA-53). A start the person set, or
  // cleared, is left alone until the next save (QA-49).
  const [saveSeq, setSaveSeq] = useState(0);
  // Closing the preference hides the optional range; a start typed by hand
  // stays on the form and still shows as the "at …" chip.
  useEffect(() => {
    if (!context.showTimeOfDay) setTimesOpen(false);
  }, [context.showTimeOfDay]);
  useEffect(() => {
    setForm((prev) => {
      const untouched = prev.startTime === "" || prev.startTimeAutoFilled;
      if (!untouched) return prev;
      if (!rollOnStart) {
        return prev.startTime === ""
          ? prev
          : { ...prev, startTime: "", startTimeAutoFilled: false };
      }
      if (prev.startTime === rollOnStart) return prev;
      return { ...prev, startTime: rollOnStart, startTimeAutoFilled: true };
    });
  }, [rollOnStart, date, saveSeq, setForm]);

  // Job options with the user's recent matters listed first.
  const jobOptions = useMemo(() => {
    const recentRank = new Map(
      context.recentJobIds.map((id, i) => [id as string, i]),
    );
    const sorted = [...context.jobs].sort((a, b) => {
      const ra = recentRank.get(a._id) ?? Infinity;
      const rb = recentRank.get(b._id) ?? Infinity;
      if (ra !== rb) return ra - rb;
      return a.name.localeCompare(b.name);
    });
    return sorted.map((j) => ({
      value: j._id as string,
      label: j.jobCode ? `${j.jobCode} — ${j.name}` : j.name,
      title: j.name,
    }));
  }, [context.jobs, context.recentJobIds]);

  // Default the project code from the selected job's phase: pre-appointment
  // jobs record PRE time, appointed jobs POST (still editable per entry).
  const handleJobChange = (value: string) => {
    const job = context.jobs.find((j) => j._id === value);
    setForm((prev) => ({
      ...prev,
      jobId: value,
      projectCode: job
        ? job.status === "pre_appointment"
          ? "pre"
          : "post"
        : prev.projectCode,
    }));
  };

  // Per-job view: pin the compose row to this matter (and its phase-derived
  // project code) so every new draft is filed against the job being viewed.
  useEffect(() => {
    if (!lockedJobId || form.jobId === (lockedJobId as string)) return;
    handleJobChange(lockedJobId as string);
    // handleJobChange is stable enough for this one-shot lock; re-running on
    // lockedJobId / current jobId is what matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockedJobId, form.jobId]);

  // Dictation appends to the narrative (MOR-1456); Add waits for the final
  // transcript to land.
  const dictation = useDictatedField({
    value: narrative,
    onChange: (next) => setForm((prev) => ({ ...prev, narrative: next })),
  });

  // The end of a timed entry is its start plus its units unless that end
  // was typed. Units stay the billable duration either way.
  const effectiveEndTime =
    !endTouched && form.startTime && unitsValid
      ? addMinutesToClock(
          form.startTime,
          roundUnits(unitsNumber, context.roundingMode) * context.unitMinutes,
        )
      : form.endTime;

  // A rolled-on start sits in the form before the fields are opened. It is
  // only recorded once the person adds a timeframe, or when Show times is
  // off and they kept a start they typed.
  const recordingTimes = timesOpen || !context.showTimeOfDay;

  const closeTimes = () => {
    setTimesOpen(false);
    setEndTouched(false);
    setForm((prev) => ({
      ...prev,
      startTime: rollOnStart ?? "",
      endTime: "",
      startTimeAutoFilled: rollOnStart !== null,
    }));
  };

  const canSave =
    !!jobId &&
    !!categoryId &&
    !!narrative.trim() &&
    roundUnits(Number(units), context.roundingMode) >= 1 &&
    !dictation.voice.isActive;

  const handleSave = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    try {
      await createEntry({
        jobId: jobId as Id<"jobs">,
        date,
        startTime:
          recordingTimes && form.startTime ? form.startTime : undefined,
        endTime:
          recordingTimes && form.startTime && effectiveEndTime
            ? effectiveEndTime
            : undefined,
        categoryId: categoryId as Id<"timeCategories">,
        subCategory: form.subCategory.trim() || undefined,
        narrative: narrative.trim(),
        units: roundUnits(Number(units), context.roundingMode),
        billable,
        projectCode,
      });
      setForm((prev) => ({
        ...prev,
        units: "",
        narrative: "",
        startTime: "",
        endTime: "",
        startTimeAutoFilled: false,
      }));
      setEndTouched(false);
      setTimesOpen(false);
      setSaveSeq((n) => n + 1);
      unitsRef.current?.focus();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not save the entry."));
    } finally {
      setSaving(false);
    }
  };

  const handleDuplicate = (entry: WeekEntry) => {
    // The inline compose row records matter time; a firm activity has no
    // matter and defaults to a post-appointment code if reused here. Its
    // category is firm-scoped too (not in the matter category list), so the
    // duplicate must be re-coded rather than carry it across.
    const isFirm = entry.kind === "firm";
    setForm((prev) => ({
      jobId: (entry.jobId as string | undefined) ?? "",
      categoryId: isFirm ? "" : (entry.categoryId as string),
      subCategory: isFirm ? "" : (entry.subCategory ?? ""),
      units: String(entry.units),
      narrative: entry.narrative,
      billable: entry.billable,
      projectCode: entry.projectCode ?? "post",
      // A duplicate is new work, so it rolls on like any other entry rather
      // than copying the original's clock time.
      startTime: context.showTimeOfDay ? prev.startTime : "",
      endTime: "",
      startTimeAutoFilled: context.showTimeOfDay
        ? prev.startTimeAutoFilled
        : false,
    }));
    setEndTouched(false);
    narrativeRef.current?.focus();
  };

  const handleDelete = async (entry: WeekEntry) => {
    try {
      await deleteEntry({ entryId: entry._id });
      toast.success("Entry deleted.");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not delete the entry."));
    }
  };

  // Newest day first, so today's fresh drafts sit right under the entry
  // row instead of at the bottom of a long week/month list.
  const sortedEntries = [...entries].sort((a, b) =>
    b.date.localeCompare(a.date),
  );

  const handleRowSubmit = async (entry: WeekEntry) => {
    try {
      await submitEntry({ entryId: entry._id });
      toast.success("Entry submitted for approval.");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not submit the entry."));
    }
  };

  return (
    <div className="overflow-hidden rounded-xl bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)]">
      <div className="flex items-center justify-between border-b border-border/50 px-4 py-3">
        <h2 className="text-sm font-semibold">
          {rangeLabel ??
            `Drafts — ${format(parseISO(date), "EEEE d MMMM yyyy")}`}
        </h2>
        <span className="text-xs text-muted-foreground">
          {entries.length === 1 ? "1 entry" : `${entries.length} entries`}
          {" · "}
          {fmtHours(entries.reduce((sum, e) => sum + e.units, 0))}
        </span>
      </div>

      {/* Bulk edit toolbar — appears when editable entries are selected */}
      {selectedIds.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 border-b border-border/50 bg-muted/40 px-4 py-2">
          <span className="text-xs font-medium">
            {selectedIds.length} selected
          </span>
          <Select
            value=""
            onValueChange={(value) =>
              void runBulk(
                () =>
                  updateEntries({
                    entryIds: selectedIds,
                    patch: { categoryId: value as Id<"timeCategories"> },
                  }),
                "recategorised",
              )
            }
          >
            <SelectTrigger className="h-7 w-44 text-xs">
              <SelectValue placeholder="Set category…" />
            </SelectTrigger>
            <SelectContent>
              {context.categories.map((c) => (
                <SelectItem key={c._id} value={c._id as string}>
                  <span className="flex items-center gap-1.5">
                    <CategoryGlyph color={c.color} icon={c.icon} />
                    {c.name}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={() =>
              void runBulk(
                () =>
                  updateEntries({
                    entryIds: selectedIds,
                    patch: { billable: true },
                  }),
                "made billable",
              )
            }
          >
            Billable
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={() =>
              void runBulk(
                () =>
                  updateEntries({
                    entryIds: selectedIds,
                    patch: { billable: false },
                  }),
                "made non-billable",
              )
            }
          >
            Non-billable
          </Button>
          <div className="flex items-center gap-1">
            <span className="text-xs text-muted-foreground">Move to</span>
            <DatePicker
              value=""
              onChange={(iso) => {
                if (iso) {
                  void runBulk(
                    () =>
                      updateEntries({
                        entryIds: selectedIds,
                        patch: { date: iso },
                      }),
                    "moved",
                  );
                }
              }}
              placeholder="date"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={() =>
              void runBulk(async () => {
                await Promise.all(
                  selectedIds.map((entryId) => submitEntry({ entryId })),
                );
                return selectedIds.length;
              }, "submitted for approval")
            }
          >
            <Send className="size-3.5" />
            Submit
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs text-destructive"
            onClick={() =>
              void runBulk(
                () => deleteEntries({ entryIds: selectedIds }),
                "deleted",
              )
            }
          >
            <Trash2 className="size-3.5" />
            Delete
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs"
            onClick={() => setSelected(new Set())}
          >
            Clear
          </Button>
        </div>
      )}

      <div className="overflow-x-auto">
        <Table className="table-fixed">
          <TableHeader>
            <TableRow>
              <TableHead className="w-8">
                <Checkbox
                  checked={
                    editableEntries.length > 0 &&
                    selectedIds.length === editableEntries.length
                  }
                  disabled={editableEntries.length === 0}
                  onCheckedChange={() =>
                    setSelected(
                      selectedIds.length === editableEntries.length
                        ? new Set()
                        : new Set(editableEntries.map((e) => e._id as string)),
                    )
                  }
                  aria-label="Select all editable entries"
                />
              </TableHead>
              {showDateColumn && (
                <TableHead className="w-24 whitespace-nowrap">Date</TableHead>
              )}
              <TableHead className="w-28 whitespace-nowrap">Time</TableHead>
              <TableHead className="w-[320px]">Matter</TableHead>
              <TableHead className="w-[150px]">Category</TableHead>
              <TableHead className="w-24 whitespace-nowrap">Units</TableHead>
              <TableHead className="w-20 whitespace-nowrap text-right">
                Rate
              </TableHead>
              <TableHead className="w-24 whitespace-nowrap text-right">
                Amount
              </TableHead>
              <TableHead className="min-w-[280px]">Narrative</TableHead>
              <TableHead className="w-16">Billable</TableHead>
              <TableHead className="w-20">Project</TableHead>
              <TableHead className="w-24">Status</TableHead>
              <TableHead className="w-32" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {/* New entry row — always ready at the top, so adding
                time never means scrolling past the period's list */}
            {allowCompose && (
              <TableRow className="bg-muted/30">
                <TableCell className="align-top" />
                {showDateColumn && (
                  <TableCell className="align-top text-xs tabular-nums text-muted-foreground">
                    {/*
                    Same height as the matter picker so the date sits on
                    the control row rather than the top of this tall cell.
                  */}
                    <div className="flex h-8 items-center">
                      {format(parseISO(date), "EEE d")}
                    </div>
                  </TableCell>
                )}
                <TableCell className="px-2 py-2 align-top">
                  {/* A timeframe is optional, so the empty row offers a plus
                    rather than start and end fields. The end follows the
                    start plus the units until it is edited (MOR-935). */}
                  {context.showTimeOfDay && !timesOpen ? (
                    <div className="flex h-8 items-center">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="size-7 text-muted-foreground"
                        aria-label="Add start and end time"
                        onClick={() => setTimesOpen(true)}
                      >
                        <Plus className="size-3.5" />
                      </Button>
                    </div>
                  ) : context.showTimeOfDay ? (
                    <div className="flex w-full min-w-0 flex-col gap-1">
                      <TimePicker
                        value={form.startTime || undefined}
                        onChange={(value) => {
                          setForm((prev) => ({
                            ...prev,
                            startTime: value,
                            startTimeAutoFilled: false,
                            ...(value === "" ? { endTime: "" } : {}),
                          }));
                          if (value === "") setEndTouched(false);
                        }}
                        placeholder="Start"
                        clearable
                        clearLabel="Clear start time"
                        inputAriaLabel="Start time"
                        compact
                      />
                      <TimePicker
                        value={effectiveEndTime || undefined}
                        onChange={(value) => {
                          setEndTouched(true);
                          setForm((prev) => ({ ...prev, endTime: value }));
                        }}
                        placeholder="End"
                        clearable
                        clearLabel="Clear end time"
                        inputAriaLabel="End time"
                        compact
                      />
                      <button
                        type="button"
                        onClick={closeTimes}
                        className="self-start text-[10px] text-muted-foreground hover:text-foreground"
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <div className="flex h-8 items-center">
                      {form.startTime ? (
                        <span className="flex w-fit items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-[10px] tabular-nums text-muted-foreground">
                          at {form.startTime}
                          <button
                            type="button"
                            onClick={() =>
                              setForm((prev) => ({
                                ...prev,
                                startTime: "",
                                startTimeAutoFilled: false,
                              }))
                            }
                            aria-label="Clear start time"
                          >
                            <X className="size-2.5" />
                          </button>
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground/50">
                          —
                        </span>
                      )}
                    </div>
                  )}
                </TableCell>
                <TableCell className="align-top">
                  {lockedJobId ? (
                    <div className="flex h-9 items-center truncate rounded-md border border-input bg-muted/40 px-3 text-sm text-muted-foreground">
                      {jobOptions.find(
                        (o) => o.value === (lockedJobId as string),
                      )?.label ?? "This matter"}
                    </div>
                  ) : (
                    <SearchableSelect
                      value={jobId}
                      onValueChange={handleJobChange}
                      options={jobOptions}
                      placeholder="Select matter…"
                      searchPlaceholder="Search matters…"
                      emptyMessage="No matters found."
                      className="w-full"
                    />
                  )}
                </TableCell>
                <TableCell className="align-top">
                  <Select
                    value={categoryId}
                    onValueChange={(value) =>
                      setForm((prev) => ({
                        ...prev,
                        categoryId: value,
                        // Sub-codes belong to one head category, so a leftover
                        // value would be filed under a category that never
                        // had it.
                        subCategory: "",
                      }))
                    }
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="Category…" />
                    </SelectTrigger>
                    <SelectContent>
                      {context.categories.map((c) => (
                        <SelectItem key={c._id} value={c._id as string}>
                          <span className="flex items-center gap-1.5">
                            <CategoryGlyph color={c.color} icon={c.icon} />
                            {c.name}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <SubCategoryPicker
                    categoryId={categoryId as Id<"timeCategories"> | ""}
                    categories={context.categories}
                    value={form.subCategory}
                    onChange={(next) =>
                      setForm((prev) => ({ ...prev, subCategory: next }))
                    }
                    className="mt-1 h-7 w-full"
                  />
                </TableCell>
                <TableCell className="align-top">
                  <Input
                    ref={unitsRef}
                    type="number"
                    min={1}
                    step={1}
                    value={units}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, units: e.target.value }))
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        narrativeRef.current?.focus();
                      }
                    }}
                    placeholder="Units"
                    className="h-9 w-20 tabular-nums"
                  />
                  {units && Number(units) >= 1 && (
                    <span className="mt-1 block text-xs tabular-nums text-muted-foreground">
                      {fmtHours(Number(units))}
                    </span>
                  )}
                </TableCell>
                <TableCell className="whitespace-nowrap text-right align-top text-sm tabular-nums text-muted-foreground">
                  <div className="flex h-9 items-center justify-end">
                    {unitsValid && previewRate !== undefined
                      ? formatAud(previewRate)
                      : ""}
                  </div>
                </TableCell>
                <TableCell className="whitespace-nowrap text-right align-top text-sm tabular-nums">
                  <div className="flex h-9 items-center justify-end">
                    {previewAmount !== undefined
                      ? formatAud(previewAmount)
                      : ""}
                  </div>
                </TableCell>
                <TableCell className="align-top">
                  <div className="flex items-center gap-1">
                    <Input
                      ref={narrativeRef}
                      value={dictation.displayValue}
                      readOnly={dictation.readOnly}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          narrative: e.target.value,
                        }))
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          void handleSave();
                        }
                      }}
                      placeholder="What did you work on?"
                      className="h-9"
                      {...NARRATIVE_INPUT_PROPS}
                    />
                    <DictationButton voice={dictation.voice} />
                  </div>
                  <NarrativeWarningNote
                    narrative={narrative}
                    units={Number(units)}
                    unitMinutes={context.unitMinutes}
                    orgId={orgId}
                    className="mt-1"
                  />
                </TableCell>
                <TableCell className="align-top">
                  <Switch
                    checked={billable}
                    onCheckedChange={(checked) =>
                      setForm((prev) => ({ ...prev, billable: checked }))
                    }
                  />
                </TableCell>
                <TableCell className="align-top">
                  <Select
                    value={projectCode}
                    onValueChange={(value) =>
                      setForm((prev) => ({
                        ...prev,
                        projectCode: value as ProjectCode,
                      }))
                    }
                  >
                    <SelectTrigger className="h-9 w-20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="post">POST</SelectItem>
                      <SelectItem value="pre">PRE</SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell className="align-top" />
                <TableCell className="align-top">
                  <Button
                    size="sm"
                    className="h-9"
                    disabled={!canSave || saving}
                    onClick={() => void handleSave()}
                  >
                    {saving ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <Plus className="size-4" />
                    )}
                    Add
                  </Button>
                </TableCell>
              </TableRow>
            )}

            {sortedEntries.map((entry) => (
              <EntryRow
                key={entry._id}
                entry={entry}
                showDate={showDateColumn}
                context={context}
                orgId={orgId}
                fmtHours={fmtHours}
                amount={
                  entry.hourlyRate !== undefined && entry.billable
                    ? ((entry.units * context.unitMinutes) / 60) *
                      entry.hourlyRate
                    : undefined
                }
                selected={selected.has(entry._id as string)}
                onToggleSelected={() => toggleSelected(entry._id as string)}
                onOpenDetail={() => onOpenDetail(entry._id)}
                onPatch={(patch) =>
                  updateEntry({ entryId: entry._id, patch }).catch((err) =>
                    toast.error(
                      extractErrorMessage(err, "Could not update the entry."),
                    ),
                  )
                }
                onDuplicate={() => handleDuplicate(entry)}
                onDelete={() => void handleDelete(entry)}
                onSubmit={() => void handleRowSubmit(entry)}
              />
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Single entry row — inline editable while draft/rejected, locked otherwise
// ---------------------------------------------------------------------------

function EntryRow({
  entry,
  showDate = false,
  context,
  orgId,
  fmtHours,
  amount,
  selected,
  onToggleSelected,
  onOpenDetail,
  onPatch,
  onDuplicate,
  onDelete,
  onSubmit,
}: {
  entry: WeekEntry;
  showDate?: boolean;
  context: EntryContext;
  orgId: Id<"organizations">;
  fmtHours: (units: number) => string;
  /** Billable dollar value at the entry's rate, when a rate is known. */
  amount?: number;
  selected: boolean;
  onToggleSelected: () => void;
  onOpenDetail: () => void;
  onPatch: (patch: {
    categoryId?: Id<"timeCategories">;
    subCategory?: string | null;
    narrative?: string;
    units?: number;
    billable?: boolean;
    projectCode?: ProjectCode;
  }) => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onSubmit: () => void;
}) {
  const editable = entry.status === "draft" || entry.status === "rejected";
  const isFirm = entry.kind === "firm";
  // Firm time is coded against a firm activity (a firm-scoped category), so
  // the picker and badge lookups follow the entry's kind.
  const categoryList = isFirm ? context.firmActivities : context.categories;
  const [narrative, setNarrative] = useState(entry.narrative);
  const [units, setUnits] = useState(String(entry.units));
  const [subCategory, setSubCategory] = useState(entry.subCategory ?? "");

  // Sync local state when the server value changes (reactive updates).
  useEffect(() => setNarrative(entry.narrative), [entry.narrative]);
  useEffect(() => setUnits(String(entry.units)), [entry.units]);
  useEffect(() => setSubCategory(entry.subCategory ?? ""), [entry.subCategory]);

  const commitNarrative = () => {
    const trimmed = narrative.trim();
    if (trimmed && trimmed !== entry.narrative) onPatch({ narrative: trimmed });
    else setNarrative(entry.narrative);
  };
  const commitUnits = () => {
    const value = roundUnits(Number(units), context.roundingMode);
    if (Number.isFinite(value) && value >= 1 && value !== entry.units) {
      onPatch({ units: value });
    } else setUnits(String(entry.units));
  };

  const badge = STATUS_BADGE[entry.status];

  return (
    <TableRow className={cn(!editable && "opacity-70")}>
      <TableCell>
        {editable ? (
          <Checkbox
            checked={selected}
            onCheckedChange={onToggleSelected}
            aria-label="Select entry"
          />
        ) : null}
      </TableCell>
      {showDate && (
        <TableCell className="whitespace-nowrap text-xs tabular-nums text-muted-foreground">
          {format(parseISO(entry.date), "EEE d")}
        </TableCell>
      )}
      <TableCell className="whitespace-nowrap">
        <EntryTimeRange entry={entry} unitMinutes={context.unitMinutes} />
      </TableCell>
      <TableCell>
        <button
          type="button"
          onClick={onOpenDetail}
          className="flex w-full min-w-0 flex-col text-left hover:underline"
          title="Open entry details, history and notes"
        >
          {isFirm ? (
            <span className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
              <Building2 className="size-3.5 shrink-0" />
              Firm activity
            </span>
          ) : (
            <>
              <span className="truncate text-sm font-medium">
                {entry.jobName}
              </span>
              {entry.jobCode && (
                <span className="truncate text-xs text-muted-foreground">
                  {entry.jobCode}
                </span>
              )}
            </>
          )}
        </button>
      </TableCell>
      <TableCell>
        {editable ? (
          <>
            <Select
              value={entry.categoryId as string}
              onValueChange={(value) => {
                const next = value as Id<"timeCategories">;
                // A sub-code belongs to one head category, so it is dropped
                // unless the new category defines it too.
                const keeps = categoryList
                  .find((c) => c._id === next)
                  ?.subCodes.some((s) => s.code === entry.subCategory);
                onPatch({
                  categoryId: next,
                  ...(entry.subCategory && !keeps ? { subCategory: null } : {}),
                });
              }}
            >
              <SelectTrigger className="h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {categoryList.map((c) => (
                  <SelectItem key={c._id} value={c._id as string}>
                    <span className="flex items-center gap-1.5">
                      <CategoryGlyph color={c.color} icon={c.icon} />
                      {c.name}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <SubCategoryPicker
              categoryId={entry.categoryId}
              categories={categoryList}
              value={subCategory}
              onChange={(next) => {
                setSubCategory(next);
                if (next !== (entry.subCategory ?? "")) {
                  onPatch({ subCategory: next || null });
                }
              }}
              className="mt-1 h-7 w-full"
            />
          </>
        ) : (
          <div className="flex flex-col items-start gap-0.5">
            <CategoryBadge
              category={{
                name: entry.categoryName,
                color: categoryList.find((c) => c._id === entry.categoryId)
                  ?.color,
                icon: categoryList.find((c) => c._id === entry.categoryId)
                  ?.icon,
                ipsCode: categoryList.find((c) => c._id === entry.categoryId)
                  ?.ipsCode,
              }}
            />
            {entry.subCategory && (
              <span className="text-xs text-muted-foreground">
                {entry.subCategory}
              </span>
            )}
          </div>
        )}
      </TableCell>
      <TableCell>
        {editable ? (
          // The hours caption hangs below the input without taking flow
          // height, so the input stays vertically centred with the rate.
          <div className="relative w-20">
            <Input
              type="number"
              min={1}
              step={1}
              value={units}
              onChange={(e) => setUnits(e.target.value)}
              onBlur={commitUnits}
              onKeyDown={(e) => {
                if (e.key === "Enter") e.currentTarget.blur();
              }}
              className="h-8 w-20 tabular-nums"
            />
            <span className="absolute left-0 top-full mt-0.5 whitespace-nowrap text-xs text-muted-foreground">
              {fmtHours(entry.units)}
            </span>
          </div>
        ) : (
          <span className="text-sm tabular-nums">
            {entry.units}{" "}
            <span className="text-xs text-muted-foreground">
              {fmtHours(entry.units)}
            </span>
          </span>
        )}
      </TableCell>
      <TableCell className="whitespace-nowrap text-right text-sm tabular-nums text-muted-foreground">
        {entry.hourlyRate !== undefined ? formatAud(entry.hourlyRate) : "—"}
      </TableCell>
      <TableCell className="whitespace-nowrap text-right text-sm tabular-nums">
        {amount !== undefined ? formatAud(amount) : "—"}
      </TableCell>
      <TableCell>
        {editable ? (
          <Input
            value={narrative}
            onChange={(e) => setNarrative(e.target.value)}
            onBlur={commitNarrative}
            onKeyDown={(e) => {
              if (e.key === "Enter") e.currentTarget.blur();
            }}
            className="h-8"
            {...NARRATIVE_INPUT_PROPS}
          />
        ) : (
          <span className="text-sm">{entry.narrative}</span>
        )}
        <RateWorkingNote working={entry} labelled className="mt-1" />
        {editable && (
          <NarrativeWarningNote
            narrative={narrative}
            units={Number(units)}
            unitMinutes={context.unitMinutes}
            orgId={orgId}
            className="mt-1"
          />
        )}
      </TableCell>
      <TableCell>
        {/* Firm time is never billed, so no billable flag applies. */}
        {isFirm ? (
          <span className="text-xs text-muted-foreground">—</span>
        ) : editable ? (
          <Switch
            checked={entry.billable}
            onCheckedChange={(checked) => onPatch({ billable: checked })}
          />
        ) : entry.billable ? (
          <Check className="size-4 text-emerald-600" />
        ) : (
          <span className="text-xs text-muted-foreground">No</span>
        )}
      </TableCell>
      <TableCell>
        {/* Firm time carries no matter phase, so no project code applies. */}
        {isFirm ? (
          <span className="text-xs text-muted-foreground">—</span>
        ) : editable ? (
          <Select
            value={entry.projectCode}
            onValueChange={(value) =>
              onPatch({ projectCode: value as ProjectCode })
            }
          >
            <SelectTrigger className="h-8 w-20">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="post">POST</SelectItem>
              <SelectItem value="pre">PRE</SelectItem>
            </SelectContent>
          </Select>
        ) : (
          <span className="text-xs uppercase">{entry.projectCode}</span>
        )}
      </TableCell>
      <TableCell>
        {entry.status === "rejected" && entry.rejectionReason ? (
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge className={cn("cursor-help", badge.className)}>
                  {badge.label}
                </Badge>
              </TooltipTrigger>
              <TooltipContent className="max-w-64">
                {entry.rejectionReason}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <Badge className={badge.className}>{badge.label}</Badge>
        )}
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1">
          {editable && (
            <Button
              variant="ghost"
              size="icon"
              className="size-7 text-primary"
              onClick={onSubmit}
              aria-label="Submit this entry for approval"
              title="Submit for approval"
            >
              <Send className="size-3.5" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            onClick={onOpenDetail}
            aria-label="Open entry details"
            title="Details, history and notes"
          >
            <Expand className="size-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            onClick={onDuplicate}
            aria-label="Duplicate entry"
          >
            <Copy className="size-3.5" />
          </Button>
          {editable && (
            <Button
              variant="ghost"
              size="icon"
              className="size-7 text-muted-foreground hover:text-destructive"
              onClick={onDelete}
              aria-label="Delete entry"
            >
              <Trash2 className="size-3.5" />
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}

// ---------------------------------------------------------------------------
// Locked entries — submitted & approved, read-only (drafts live above)
// ---------------------------------------------------------------------------

function LockedEntriesTable({
  entries,
  showDateColumn,
  dateFormat = "EEE d",
  title,
  emptyLabel,
  context,
  fmtHours,
  onOpenDetail,
}: {
  entries: WeekEntry[];
  showDateColumn: boolean;
  dateFormat?: string;
  title?: string;
  emptyLabel?: string;
  context: EntryContext;
  fmtHours: (units: number) => string;
  onOpenDetail: (entryId: Id<"timeEntries">) => void;
}) {
  const totalUnits = entries.reduce((sum, e) => sum + e.units, 0);
  return (
    <div className="overflow-hidden rounded-xl bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)]">
      <div className="flex items-center justify-between border-b border-border/50 px-4 py-3">
        <h2 className="text-sm font-semibold">
          {title ?? "Submitted & approved"}
        </h2>
        <span className="text-xs text-muted-foreground">
          {entries.length === 1 ? "1 entry" : `${entries.length} entries`}
          {" · "}
          {fmtHours(totalUnits)}
        </span>
      </div>
      <div className="overflow-x-auto">
        <Table className="table-fixed">
          <TableHeader>
            {/*
              The column grid mirrors the drafts table above (DayEntriesTable)
              so the two line up: a leading spacer stands in for the drafts'
              select checkbox and a trailing spacer for its row actions, and
              every shared column keeps the same width.
            */}
            <TableRow>
              <TableHead className="w-8" />
              {showDateColumn && (
                <TableHead className="w-24 whitespace-nowrap">Date</TableHead>
              )}
              <TableHead className="w-28 whitespace-nowrap">Time</TableHead>
              <TableHead className="w-[320px]">Matter</TableHead>
              <TableHead className="w-[150px]">Category</TableHead>
              <TableHead className="w-24 whitespace-nowrap">Units</TableHead>
              <TableHead className="w-20 whitespace-nowrap text-right">
                Rate
              </TableHead>
              <TableHead className="w-24 whitespace-nowrap text-right">
                Amount
              </TableHead>
              <TableHead className="min-w-[280px]">Narrative</TableHead>
              <TableHead className="w-16">Billable</TableHead>
              <TableHead className="w-20">Project</TableHead>
              <TableHead className="w-24">Status</TableHead>
              <TableHead className="w-32" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {entries.length === 0 && emptyLabel ? (
              <TableRow>
                <TableCell
                  colSpan={showDateColumn ? 13 : 12}
                  className="py-10 text-center text-sm text-muted-foreground"
                >
                  {emptyLabel}
                </TableCell>
              </TableRow>
            ) : null}
            {entries.map((entry) => {
              const isFirm = entry.kind === "firm";
              const category = (
                isFirm ? context.firmActivities : context.categories
              ).find((c) => c._id === entry.categoryId);
              const amount =
                entry.hourlyRate !== undefined && entry.billable
                  ? ((entry.units * context.unitMinutes) / 60) *
                    entry.hourlyRate
                  : undefined;
              const badge = STATUS_BADGE[entry.status];
              return (
                <TableRow
                  key={entry._id}
                  className="cursor-pointer"
                  onClick={() => onOpenDetail(entry._id)}
                >
                  <TableCell />
                  {showDateColumn && (
                    <TableCell className="whitespace-nowrap text-xs tabular-nums text-muted-foreground">
                      {format(parseISO(entry.date), dateFormat)}
                    </TableCell>
                  )}
                  <TableCell className="whitespace-nowrap">
                    <EntryTimeRange
                      entry={entry}
                      unitMinutes={context.unitMinutes}
                    />
                  </TableCell>
                  <TableCell>
                    {isFirm ? (
                      <span className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
                        <Building2 className="size-3.5 shrink-0" />
                        Firm activity
                      </span>
                    ) : (
                      <div className="flex min-w-0 flex-col">
                        <span className="truncate text-sm font-medium">
                          {entry.jobName}
                        </span>
                        {entry.jobCode && (
                          <span className="truncate text-xs text-muted-foreground">
                            {entry.jobCode}
                          </span>
                        )}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <CategoryBadge
                      category={{
                        name: entry.categoryName,
                        color: category?.color,
                        icon: category?.icon,
                        ipsCode: category?.ipsCode,
                      }}
                    />
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-right text-sm tabular-nums">
                    {entry.units}{" "}
                    <span className="text-xs text-muted-foreground">
                      {fmtHours(entry.units)}
                    </span>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-right text-sm tabular-nums text-muted-foreground">
                    {entry.hourlyRate !== undefined
                      ? formatAud(entry.hourlyRate)
                      : "—"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-right text-sm tabular-nums">
                    {amount !== undefined ? formatAud(amount) : "—"}
                  </TableCell>
                  <TableCell className="max-w-96 text-sm">
                    <p className="truncate">{entry.narrative}</p>
                    <RateWorkingNote working={entry} labelled />
                  </TableCell>
                  <TableCell>
                    {/* Firm time is never billed, so no billable flag. */}
                    {isFirm ? (
                      <span className="text-xs text-muted-foreground">—</span>
                    ) : entry.billable ? (
                      <Check className="size-4 text-emerald-600" />
                    ) : (
                      <span className="text-xs text-muted-foreground">No</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {/* Firm time carries no matter phase. */}
                    {isFirm ? (
                      <span className="text-xs text-muted-foreground">—</span>
                    ) : (
                      <span className="text-xs uppercase">
                        {entry.projectCode}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge className={badge.className}>{badge.label}</Badge>
                  </TableCell>
                  <TableCell />
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
